import { INPUT_MAX_LENGTH } from "@/components/AIComponents/ExportablesModal";
import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import { type TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { type FactOpinionSorterFormData } from "@/types/models/Exportables.interface";
import {
  cell,
  cellReason,
  containerStyles,
  dataRow,
  headerCell,
  headerCellReason,
  headerRow,
  tableContainer,
} from "./FactOpinionSorter.styles";

const isFactOpinionSorterFormData = (
  data: any,
): data is FactOpinionSorterFormData => {
  return (
    data &&
    typeof data === "object" &&
    ("entries" in data ||
      "articleShortTitle" in data ||
      "article_title" in data)
  );
};

const MIN_ROWS = 4;
const DEFAULT_ENTRY = { statement: "", classification: "", explanation: "" };

const FactOpinionSorter = ({
  formData,
  onChange,
  title = "Fact vs. opinion sorter",
  isStudent = false,
}: TemplateProps<FactOpinionSorterFormData>) => {
  const data = isFactOpinionSorterFormData(formData)
    ? formData
    : ({} as FactOpinionSorterFormData);

  const articleTitle = data?.article_title || data?.articleShortTitle || "";
  const {
    topic,
    directions = `Read each statement about ${topic} based on the article ${articleTitle}. For each statement, identify if the statement is a fact or an opinion and explain your reasoning.`,
  } = data;

  const statements = data.statements ?? [];

  const paddedEntries = Array.from({
    length: statements.length || MIN_ROWS,
  }).map((_, i) => statements[i] ?? { ...DEFAULT_ENTRY });

  const handleChange = (update: Partial<FactOpinionSorterFormData>) => {
    if (!onChange) return;
    onChange(update);
  };

  const updateEntry = (
    idx: number,
    field: keyof (typeof paddedEntries)[number],
    value: string,
  ) => {
    const updated = paddedEntries.map((entry, i) =>
      i === idx ? { ...entry, [field]: value } : { ...entry },
    );
    handleChange({ statements: updated });
  };

  return (
    <div css={containerStyles}>
      <ExportablesTemplateHeaderSection
        templateTitle={title}
        directions={
          <OrganizerDirections
            name="Fact vs Opinion Sorter"
            value={directions}
            handleChange={(e) => {
              handleChange({ directions: e.target.value });
            }}
            isStudent={isStudent}
          />
        }
        articleTitle={articleTitle || undefined}
      />
      <div css={tableContainer}>
        {/* Header Row */}
        <div css={headerRow}>
          <div css={headerCell}>Statement</div>
          <div css={headerCell}>Fact or Opinion?</div>
          <div css={headerCellReason}>Why?</div>
        </div>
        {/* Data Rows */}
        {paddedEntries.map((entry, idx) => {
          const statement =
            entry.statement?.substring(0, INPUT_MAX_LENGTH) || "";
          const answer =
            entry.classification?.substring(0, INPUT_MAX_LENGTH) || "";
          const reason =
            entry.explanation?.substring(0, INPUT_MAX_LENGTH) || "";

          return (
            <div css={dataRow} key={idx}>
              {/* Statement */}
              <TemplateTextArea
                value={statement}
                readOnly={isStudent}
                maxLength={INPUT_MAX_LENGTH}
                css={cell}
              >
                <TemplateTextArea.TextArea
                  id={`fact-opinion-statement-${idx}`}
                  data-qa-selector={`exportables-fact-opinion-statement-${idx}`}
                  onChange={(e) => {
                    updateEntry(idx, "statement", e.target.value);
                  }}
                  aria-label={`Statement ${idx + 1}`}
                  rows={2}
                />
                <TemplateTextArea.Paragraph />
                <TemplateTextArea.CharacterCount />
              </TemplateTextArea>
              {/* Fact or Opinion */}
              <TemplateTextArea
                value={answer}
                maxLength={INPUT_MAX_LENGTH}
                css={cell}
              >
                <TemplateTextArea.TextArea
                  id={`fact-opinion-answer-${idx}`}
                  data-qa-selector={`exportables-fact-opinion-answer-${idx}`}
                  onChange={(e) => {
                    updateEntry(idx, "classification", e.target.value);
                  }}
                  aria-label={`Answer ${idx + 1}`}
                  rows={2}
                />
                <TemplateTextArea.CharacterCount />
              </TemplateTextArea>
              {/* Reason */}
              <TemplateTextArea
                value={reason}
                maxLength={INPUT_MAX_LENGTH}
                css={cellReason}
              >
                <TemplateTextArea.TextArea
                  id={`fact-opinion-reason-${idx}`}
                  data-qa-selector={`exportables-fact-opinion-reason-${idx}`}
                  onChange={(e) => {
                    updateEntry(idx, "explanation", e.target.value);
                  }}
                  aria-label={`Reason ${idx + 1}`}
                  rows={2}
                />
                <TemplateTextArea.CharacterCount />
              </TemplateTextArea>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FactOpinionSorter;
